/**
 * ListaRozmow - lewa kolumna panelu.
 *
 * Zawiera: kanaly stale ("Cała sieć", "Twój sklep") na gorze BEZ mozliwosci
 * zamkniecia, ponizej rozmowy prywatne z krzyzykiem, a nad wszystkim
 * wyszukiwarke kontaktow do zaczynania nowych rozmow 1:1.
 *
 * O zamykaniu decyduje pole `zamykalna` z backendu (tylko typ='prywatna').
 * Nie zgadujemy tego po nazwie - kanaly rozpoznaje sie po `typ`/`klucz`.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { skrotCzasu } from './Wiadomosc';
import { pobierzKontakty, otworzRozmowePrywatna } from '../../services/komunikatorService';

const ikonaRozmowy = (r) => {
  if (r.typ === 'globalny') return '🌐';
  if (r.typ === 'sklep') return '🏬';
  return '👤';
};

const ListaRozmow = ({
  rozmowy = [],
  aktywnaId = null,
  ladowanie = false,
  blad = null,
  onWybierz,
  onZamknij,
  onOtwarto,
  onOdswiez,
}) => {
  const [szukanie, setSzukanie] = useState(false);
  const [fraza, setFraza] = useState('');
  const [kontakty, setKontakty] = useState([]);
  const [ladowanieKontaktow, setLadowanieKontaktow] = useState(false);
  const [tworzony, setTworzony] = useState(null);

  const zadanieRef = useRef(0);
  const zywyRef = useRef(true);
  const poleRef = useRef(null);

  useEffect(() => {
    zywyRef.current = true;
    return () => { zywyRef.current = false; };
  }, []);

  // Debounce 300 ms + token zadania: odpowiedz na stara fraze nie nadpisze nowej.
  useEffect(() => {
    if (!szukanie) return undefined;
    const mojeZadanie = zadanieRef.current + 1;
    zadanieRef.current = mojeZadanie;
    setLadowanieKontaktow(true);
    const t = setTimeout(() => {
      pobierzKontakty(fraza.trim())
        .then((lista) => {
          if (!zywyRef.current || zadanieRef.current !== mojeZadanie) return;
          setKontakty(lista);
        })
        .catch((e) => {
          if (!zywyRef.current || zadanieRef.current !== mojeZadanie) return;
          setKontakty([]);
          toast.error(e.message || 'Nie udało się pobrać kontaktów');
        })
        .finally(() => {
          if (zywyRef.current && zadanieRef.current === mojeZadanie) setLadowanieKontaktow(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [fraza, szukanie]);

  useEffect(() => {
    if (szukanie && poleRef.current) poleRef.current.focus();
  }, [szukanie]);

  const zamknijSzukanie = useCallback(() => {
    setSzukanie(false);
    setFraza('');
    setKontakty([]);
  }, []);

  const otworzDm = useCallback(async (kontakt) => {
    if (tworzony) return;
    // ZAWSZE wolamy backend, takze gdy kontakt ma juz rozmowa_id.
    // POST /rozmowy/dm jest idempotentny po kolumnie 'klucz' (przy istniejacej
    // rozmowie to dwa tanie SELECT-y), ale JAKO JEDYNY zdejmuje flage 'ukryta'
    // inicjatorowi. Skrot "mam id, wiec nie pytam serwera" sprawial, ze
    // WLASNORECZNIE zamknietej rozmowy nie dalo sie juz otworzyc: ukryta=1
    // zostawalo, GET /rozmowy nadal jej nie zwracalo, panel mrugal i wracal
    // do listy - az do momentu, gdy rozmowca napisal zwykly tekst.
    setTworzony(kontakt.login);
    try {
      const rozmowa = await otworzRozmowePrywatna(kontakt.login);
      if (!zywyRef.current) return;
      zamknijSzukanie();
      if (onOtwarto && rozmowa) onOtwarto(rozmowa);
    } catch (e) {
      if (zywyRef.current) toast.error(e.message || 'Nie udało się otworzyć rozmowy');
    } finally {
      if (zywyRef.current) setTworzony(null);
    }
  }, [tworzony, zamknijSzukanie, onOtwarto]);

  const kanaly = rozmowy.filter((r) => r.typ !== 'prywatna');
  const prywatne = rozmowy.filter((r) => r.typ === 'prywatna');

  const wiersz = (r) => {
    const aktywna = r.id === aktywnaId;
    return (
      <div
        key={r.id}
        className={`kom-rozmowa ${aktywna ? 'kom-rozmowa-aktywna' : ''}`}
        style={{
          background: aktywna ? 'var(--primary-light)' : 'transparent',
          borderLeft: `3px solid ${aktywna ? 'var(--primary)' : 'transparent'}`,
        }}
      >
        <button
          type="button"
          className="kom-rozmowa-klik"
          style={{ background: 'transparent', border: 'none' }}
          onClick={() => onWybierz && onWybierz(r.id)}
          title={r.nazwa}
        >
          <span className="kom-rozmowa-ikona">{ikonaRozmowy(r)}</span>
          <span className="kom-rozmowa-tresc">
            <span className="kom-rozmowa-gora">
              <span className="kom-rozmowa-nazwa">{r.nazwa}</span>
              <span className="kom-rozmowa-czas">{skrotCzasu(r.ostatnia_at)}</span>
            </span>
            <span className="kom-rozmowa-dol">
              <span className="kom-rozmowa-zajawka">
                {r.ostatnia_tresc
                  ? (r.ostatnia_autor ? `${r.ostatnia_autor}: ${r.ostatnia_tresc}` : r.ostatnia_tresc)
                  : 'Brak wiadomości'}
              </span>
              {r.nieprzeczytane > 0 && (
                <span className="kom-badge kom-rozmowa-licznik-alarm">
                  {r.nieprzeczytane > 99 ? '99+' : r.nieprzeczytane}
                </span>
              )}
            </span>
          </span>
        </button>

        {r.zamykalna && (
          <button
            type="button"
            className="kom-rozmowa-zamknij"
            style={{ background: 'transparent', border: 'none' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onZamknij) onZamknij(r.id);
            }}
            title="Zamknij rozmowę (wróci przy nowej wiadomości)"
            aria-label="Zamknij rozmowę"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="kom-lista">
      <div className="kom-lista-gora">
        {szukanie ? (
          <div className="kom-szukajka">
            <input
              ref={poleRef}
              type="text"
              value={fraza}
              placeholder="Szukaj osoby…"
              onChange={(e) => setFraza(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') zamknijSzukanie(); }}
            />
            <button
              type="button"
              className="kom-ikona-btn"
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
              onClick={zamknijSzukanie}
              title="Anuluj"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="kom-nowa-rozmowa"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={() => setSzukanie(true)}
          >
            + Nowa rozmowa
          </button>
        )}
      </div>

      <div className="kom-lista-przewijana">
        {szukanie ? (
          <>
            {ladowanieKontaktow && <div className="kom-info">Szukanie…</div>}
            {!ladowanieKontaktow && !kontakty.length && (
              <div className="kom-info">Brak wyników</div>
            )}
            {kontakty.map((k) => (
              <button
                key={k.login}
                type="button"
                className="kom-kontakt"
                style={{ background: 'transparent', border: 'none' }}
                onClick={() => otworzDm(k)}
                disabled={tworzony === k.login}
              >
                <span className="kom-rozmowa-ikona">{k.admin ? '🛡️' : '👤'}</span>
                <span className="kom-kontakt-nazwa">{k.nazwa || k.login}</span>
                {tworzony === k.login && <span className="kom-plik-meta">otwieram…</span>}
              </button>
            ))}
          </>
        ) : (
          <>
            {blad && (
              <div className="kom-pasek-bledu">
                <span>{blad}</span>
                <button
                  type="button"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
                  onClick={onOdswiez}
                >
                  Ponów
                </button>
              </div>
            )}
            {ladowanie && !rozmowy.length && <div className="kom-info">Wczytywanie…</div>}

            {kanaly.length > 0 && <div className="kom-sekcja">Kanały</div>}
            {kanaly.map(wiersz)}

            {prywatne.length > 0 && <div className="kom-sekcja">Rozmowy prywatne</div>}
            {prywatne.map(wiersz)}

            {!ladowanie && !rozmowy.length && !blad && (
              <div className="kom-info">Brak rozmów</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ListaRozmow;
